import { type Action, type AnyAction, combineReducers } from 'redux';
import Immutable from 'immutable';

import StoreORM from 'js/src/data/store-orm';
import {
    deleteTranscript,
    disableTranscriptOverlays,
    endLexicalControllingItsOwnState,
    endTimingMode,
    overwriteAllLexicalEditors,
    removeLexicalEditor,
    removeTranscriptTranslation,
    setSceneConversionModalVisible,
    setTranscriptCurrentTime,
    setTranscriptLanguage,
    setTranscriptTabVisibility,
    startLexicalControllingItsOwnState,
    startTimingMode,
    storeLexicalEditor,
    storeRawTranscript,
    storeTranscriptTranslations,
    toggleOriginal,
} from 'js/src/data/transcript/transcript-actions';
import type {
    LexicalEditorsDataType,
    TranscriptData,
} from 'js/src/data/transcript/transcript-types';
import { TranscriptRegionPurpose } from 'js/src/data/transcript/transcript-types';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';

export function transcripts(
    state = StoreORM.newFromIdPrefix<TranscriptData, string>('transcripts').data,
    action: AnyAction,
) {
    if (storeRawTranscript.match(action)) {
        const orm = new StoreORM<TranscriptData, string>(state);
        const transcript = orm.getByIdOrUndefined(action.payload.sceneGroupId);
        if (!transcript) {
            const transcriptData: TranscriptData = {
                sceneGroupId: action.payload.sceneGroupId,
                editorState: action.payload.editorState,
                translations: action.payload.translations,
                isOriginal: action.payload.isOriginal,
                disableOverlays: false,
                language: action.payload.language,
            };
            orm.pushJSItem(transcriptData, 'sceneGroupId');
        } else {
            orm.updateById(action.payload.sceneGroupId, {
                key: ['editorState'],
                value: action.payload.editorState,
            });
            orm.updateById(action.payload.sceneGroupId, {
                key: ['isOriginal'],
                value: action.payload.isOriginal,
            });
            if (action.payload.language) {
                orm.updateById(action.payload.sceneGroupId, {
                    key: ['language'],
                    value: action.payload.language,
                });
            }
        }
        return orm.data;
    }
    if (toggleOriginal.match(action)) {
        const orm = new StoreORM<TranscriptData, string>(state);
        const transcript = orm.getById(action.payload.sceneGroupId);
        orm.updateById(action.payload.sceneGroupId, {
            key: ['isOriginal'],
            value: !transcript.get('isOriginal'),
        });
        return orm.data;
    }
    if (setTranscriptLanguage.match(action)) {
        const orm = new StoreORM<TranscriptData, string>(state);
        orm.updateById(action.payload.sceneGroupId, {
            key: ['language'],
            value: action.payload.language,
        });
        return orm.data;
    }
    if (disableTranscriptOverlays.match(action)) {
        const orm = new StoreORM<TranscriptData, string>(state);
        orm.updateById(action.payload.sceneGroupId, {
            key: ['disableOverlays'],
            value: true,
        });
        return orm.data;
    }
    if (deleteTranscript.match(action)) {
        const orm = new StoreORM<TranscriptData, string>(state);
        orm.delete([action.payload.sceneGroupId]);
        return orm.data;
    }
    if (storeTranscriptTranslations.match(action)) {
        const orm = new StoreORM<TranscriptData, string>(state);
        orm.updateById(action.payload.sceneGroupId, {
            key: ['translations'],
            value: action.payload.translations,
        });
        return orm.data;
    }
    if (removeTranscriptTranslation.match(action)) {
        const orm = new StoreORM<TranscriptData, string>(state);
        orm.updateById(action.payload.sceneGroupId, {
            key: ['translations'],
            value: Immutable.Map(),
        });
        return orm.data;
    }
    return state;
}

export function transcript_visible(state = false, action: AnyAction) {
    if (setTranscriptTabVisibility.match(action)) {
        return action.payload;
    }
    return state;
}

export function scene_conversion_modal_visible(state = false, action: AnyAction) {
    if (setSceneConversionModalVisible.match(action)) {
        return action.payload;
    }
    return state;
}

/**
 * Holds transcript current time and the scene group id & scene id that it should belongs to
 * It is set when seeking the transcript
 *
 * @param sceneGroupId - The scene group id of the active transcript
 * @param sceneId - The scene id of the active transcript
 * @param currentTimeMillis - The current time in milliseconds relative to the start of the scene group.
 *
 * This is "after" trimming meaning time 0 is always at the start of the scene group.
 * This is the time we start scene group preview from and is different from the preview render time
 *
 * We need 2 different times because editing text on a scene (or overlay) is only possible
 * by seeking to the midpoint of the overlay even if we select the last word in the overlay.
 * However, the preview should ideally start playing from the selected word (or wherever).
 *
 * By associating the current time as the time to start preview from, we can seek luminary
 * to midpoint of overlay if word is anywhere inside overlay for text editing while saving
 * the start time of the selected word as current time to start preview from.
 *
 * Note: While luminary is playing, the current time should be read directly from luminary.currentTime, rather
 * than using the state here. That's because the currentTime is not constantly sync'd to redux for performance reasons.
 */
export function current_time(
    state: ImmutableMap<{
        sceneGroupId: string | null;
        sceneId: string | null;
        currentTimeMillis: number;
    }> = ImmutableFromJS({
        sceneGroupId: null,
        sceneId: null,
        currentTimeMillis: 0,
    }),
    action: AnyAction,
) {
    if (setTranscriptCurrentTime.match(action)) {
        return ImmutableFromJS({
            sceneGroupId: action.payload.sceneGroupId,
            sceneId: action.payload.sceneId,
            currentTimeMillis: action.payload.currentTimeMillis,
        });
    }
    return state;
}

export function lexical_editors(
    state = StoreORM.newFromIdPrefix<LexicalEditorsDataType, string>('lexical-editors').data,
    action: Action,
) {
    if (storeLexicalEditor.match(action)) {
        const orm = new StoreORM<LexicalEditorsDataType, string>(state);
        orm.pushJSItem(action.payload, 'sceneGroupId');
        return orm.data;
    }
    if (removeLexicalEditor.match(action)) {
        const orm = new StoreORM<LexicalEditorsDataType, string>(state);
        orm.delete([action.payload]);
        return orm.data;
    }
    if (overwriteAllLexicalEditors.match(action)) {
        const orm = new StoreORM<LexicalEditorsDataType, string>(state);
        orm.delete(orm.getAllIds());
        action.payload.forEach((lexicalEditorObj) => {
            orm.pushJSItem(lexicalEditorObj, 'sceneGroupId');
        });
        return orm.data;
    }
    if (startLexicalControllingItsOwnState.match(action)) {
        const orm = new StoreORM<LexicalEditorsDataType, string>(state);
        const langCode = action.payload.langCode;
        if (langCode) {
            // this redux action is meant for the translated transcript, not the main transcript
            // so we don't need to do anything here
            return state;
        }
        orm.updateById(action.payload.sceneGroupId, {
            value: true,
            key: ['lexicalControlsItsOwnState'],
        });
        return orm.data;
    }
    if (endLexicalControllingItsOwnState.match(action)) {
        const orm = new StoreORM<LexicalEditorsDataType, string>(state);
        const langCode = action.payload.langCode;
        if (langCode) {
            // this redux action is meant for the translated transcript, not the main transcript
            // so we don't need to do anything here
            return state;
        }
        orm.updateById(action.payload.sceneGroupId, {
            value: false,
            key: ['lexicalControlsItsOwnState'],
        });
        return orm.data;
    }
    return state;
}

export type TimingModeState = ImmutableMap<{
    assetId: string | null;
    sceneId: string | null;
    sceneGroupId: string | null;
    purpose: TranscriptRegionPurpose;
}>;

const initialTimingModeState: TimingModeState = ImmutableFromJS({
    assetId: null,
    sceneId: null,
    sceneGroupId: null,
    purpose: TranscriptRegionPurpose.ASSET_TIMING,
});

export function timing_mode(state: TimingModeState = initialTimingModeState, action: AnyAction) {
    if (startTimingMode.match(action)) {
        return ImmutableFromJS({
            assetId: action.payload.assetId,
            sceneId: action.payload.sceneId,
            sceneGroupId: action.payload.sceneGroupId,
            purpose: action.payload.purpose ?? TranscriptRegionPurpose.ASSET_TIMING,
        });
    }
    if (endTimingMode.match(action)) {
        return initialTimingModeState;
    }
    return state;
}

export const undoable_transcript = combineReducers({
    transcripts,
});

export const transcript = combineReducers({
    transcript_visible,
    scene_conversion_modal_visible,
    lexical_editors,
    current_time,
    timing_mode,
});

export type TranscriptState = ReturnType<typeof transcript>;
